import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { getWorkflowStepTransitionState } from '@/lib/workflow/stepState'

import type { SoftwareAssuranceWorkflowController } from '@/hooks/workflow/useSoftwareAssuranceWorkflow'

import { WorkflowStepContent } from './WorkflowStepContent'

interface WorkflowStepCardProps {
  workflow: SoftwareAssuranceWorkflowController
}

export function WorkflowStepCard({ workflow }: WorkflowStepCardProps) {
  const currentStepState = workflow.current?.id
    ? getWorkflowStepTransitionState({
        stepId: workflow.current.id,
        completedStepIds: workflow.completedSteps,
        currentStepId: workflow.current.id,
      })
    : null

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle>{workflow.current?.label ?? 'Workflow Step'}</CardTitle>
            <p className="text-sm text-muted-foreground mt-1">
              {workflow.current?.description ?? 'Complete this step to advance.'}
            </p>
          </div>
          <Badge variant="outline">
            Step {workflow.currentStep + 1} of {workflow.steps.length}
          </Badge>
        </div>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="flex flex-col gap-2 rounded-lg border border-border p-3 bg-muted/20">
          <span className="text-xs text-muted-foreground">Active Process</span>
          <Select
            value={workflow.selectedProcessId ?? ''}
            onValueChange={(value) => workflow.setSelectedProcessId(value)}
          >
            <SelectTrigger>
              <SelectValue placeholder="Select process..." />
            </SelectTrigger>
            <SelectContent>
              {workflow.processes.map((process) => (
                <SelectItem key={process.process_id} value={process.process_id}>
                  {process.name} (v{process.version})
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        {workflow.current?.id && currentStepState && (
          <div className="flex items-center justify-between rounded-lg border border-border p-3 bg-muted/10">
            <div className="text-xs text-muted-foreground">{currentStepState.statusMessage}</div>
            <Button
              variant={currentStepState.isCompleted ? 'outline' : 'default'}
              disabled={!workflow.selectedMissionId}
              onClick={() =>
                workflow.handleStepCompletion(
                  workflow.current!.id,
                  currentStepState.nextCompleted
                )
              }
            >
              {currentStepState.actionLabel}
            </Button>
          </div>
        )}
        <WorkflowStepContent workflow={workflow} />
      </CardContent>
    </Card>
  )
}
